import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  addPersonTraining,
  createCompliancePerson,
  listCompliancePeople,
  listUsers,
  setPersonCheck,
  updateCompliancePerson,
  type CheckKind,
  type PersonStatus,
  type PersonType,
  type PersonView,
} from "@/lib/api";
import { Modal } from "@/components/Modal";

const STATUS_STYLE: Record<PersonStatus, { label: string; cls: string }> = {
  pending: { label: "Pending", cls: "border border-border text-text-secondary" },
  active: { label: "Active", cls: "bg-success/15 text-success" },
  leaver: { label: "Leaver", cls: "border border-border text-text-muted" },
};

const today = () => new Date().toISOString().slice(0, 10);

/**
 * The joiner/leaver register. Auditors sample starters and leavers by name
 * and ask for the dated checklist behind each one — that's what this is.
 */
export function CompliancePeoplePage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [addOpen, setAddOpen] = useState(false);
  const [showLeavers, setShowLeavers] = useState(true);

  const { data: people, isLoading } = useQuery({
    queryKey: ["compliance-people"],
    queryFn: listCompliancePeople,
  });

  const openId = params.get("open");
  const open = useMemo(() => people?.find((p) => p.person.id === openId) ?? null, [people, openId]);
  const shown = (people ?? []).filter((p) => showLeavers || p.status !== "leaver");

  return (
    <div>
      <header className="mb-8 flex items-end justify-between gap-4">
        <div>
          <h1 className="page-title">People</h1>
          <p className="mt-2 text-text-secondary">
            Everyone with access, employee or contractor, with the dated onboarding and
            offboarding record behind each one. Kept separate from portal logins so a
            leaver's evidence outlives their account.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={showLeavers}
              onChange={(e) => setShowLeavers(e.target.checked)}
            />
            Show leavers
          </label>
          <button
            onClick={() => setAddOpen(true)}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
          >
            Add person
          </button>
        </div>
      </header>

      {isLoading ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      ) : shown.length === 0 ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-center">
          <div className="max-w-md">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-token-lg bg-accent-muted text-2xl text-accent">
              <i className="fa-solid fa-users" />
            </div>
            <h2 className="font-display text-lg font-semibold text-text-heading">
              Nobody on the register
            </h2>
            <p className="mx-auto mt-1.5 text-sm text-text-secondary">
              Add the four of you plus any contractors. Each one gets an onboarding
              checklist you can back-fill with the real dates.
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                <th className="px-4 py-2.5 font-semibold">Name</th>
                <th className="px-4 py-2.5 font-semibold">Type</th>
                <th className="px-4 py-2.5 font-semibold">Started</th>
                <th className="px-4 py-2.5 font-semibold">Left</th>
                <th className="px-4 py-2.5 font-semibold">Checklist</th>
                <th className="px-4 py-2.5 font-semibold">Trained</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((v) => {
                const kind: CheckKind = v.status === "leaver" ? "offboarding" : "onboarding";
                const rel = v.checks.filter((c) => c.kind === kind);
                const done = rel.filter((c) => c.completedOn).length;
                return (
                  <tr
                    key={v.person.id}
                    className="cursor-pointer border-b border-border-light hover:bg-bg-card-hover"
                    onClick={() => setParams({ open: v.person.id })}
                  >
                    <td className="px-4 py-2.5 font-medium text-text-heading">{v.person.name}</td>
                    <td className="px-4 py-2.5 capitalize text-text-secondary">{v.person.type}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                      {v.person.startedOn ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                      {v.person.endedOn ?? "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <span className={done === rel.length ? "text-success" : "text-warning"}>
                        {done}/{rel.length}
                      </span>
                      <span className="ml-1.5 text-xs text-text-muted">{kind}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                      {v.lastTrainingOn ?? <span className="text-warning">never</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[v.status].cls}`}
                      >
                        {STATUS_STYLE[v.status].label}
                      </span>
                      {v.blockers.length > 0 && (
                        <span className="ml-2 text-xs text-text-muted">{v.blockers.length}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && <PersonModal view={open} onClose={() => setParams({})} />}
      {addOpen && (
        <AddPersonModal
          onClose={() => setAddOpen(false)}
          onCreated={(id) => {
            void qc.invalidateQueries({ queryKey: ["compliance-people"] });
            setParams({ open: id });
          }}
        />
      )}
    </div>
  );
}

function AddPersonModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState<PersonType>("employee");
  const [role, setRole] = useState("");
  const [startedOn, setStartedOn] = useState(today());

  const create = useMutation({
    mutationFn: () =>
      createCompliancePerson({
        name: name.trim(),
        type,
        role: role.trim() || null,
        startedOn: startedOn || null,
      }),
    onSuccess: (v) => {
      onCreated(v.person.id);
      onClose();
    },
  });

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
  return (
    <Modal
      open
      onClose={onClose}
      title="Add person"
      footer={
        <button
          onClick={() => create.mutate()}
          disabled={!name.trim() || create.isPending}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          Add
        </button>
      }
    >
      <div className="space-y-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Full name"
          className={inputCls}
          autoFocus
        />
        <select value={type} onChange={(e) => setType(e.target.value as PersonType)} className={inputCls}>
          <option value="employee">Employee</option>
          <option value="contractor">Contractor</option>
          <option value="founder">Founder</option>
        </select>
        <input
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="Role (optional)"
          className={inputCls}
        />
        <label className="block text-xs text-text-muted">
          Start date
          <input
            type="date"
            value={startedOn}
            onChange={(e) => setStartedOn(e.target.value)}
            className={inputCls}
          />
        </label>
        {create.isError && <p className="text-xs text-danger">{(create.error as Error).message}</p>}
      </div>
    </Modal>
  );
}

function PersonModal({ view, onClose }: { view: PersonView; onClose: () => void }) {
  const qc = useQueryClient();
  const p = view.person;
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const [topic, setTopic] = useState("Security awareness");
  const [trainedOn, setTrainedOn] = useState(today());
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["compliance-people"] });
    void qc.invalidateQueries({ queryKey: ["compliance-overview"] });
  };
  const fail = (e: Error) => setError(e.message);

  const check = useMutation({
    mutationFn: ({ id, on }: { id: string; on: string | null }) => setPersonCheck(id, on),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const patch = useMutation({
    mutationFn: (input: Parameters<typeof updateCompliancePerson>[1]) =>
      updateCompliancePerson(p.id, input),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const train = useMutation({
    mutationFn: () => addPersonTraining(p.id, topic, trainedOn),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
  const onboarding = view.checks.filter((c) => c.kind === "onboarding");
  const offboarding = view.checks.filter((c) => c.kind === "offboarding");

  return (
    <Modal open onClose={onClose} title={p.name} wide>
      <div className="space-y-5 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[view.status].cls}`}
          >
            {STATUS_STYLE[view.status].label}
          </span>
          <span className="text-text-muted">
            {p.type}
            {p.role ? ` · ${p.role}` : ""} · started {p.startedOn ?? "—"}
          </span>
          <label className="ml-auto flex items-center gap-2 text-text-secondary">
            Portal account
            <select
              value={p.userId ?? ""}
              onChange={(e) => patch.mutate({ userId: e.target.value || null })}
              className="rounded-md border border-border bg-bg-card px-2 py-1 text-sm text-text-heading"
            >
              <option value="">— none —</option>
              {users?.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {view.blockers.length > 0 && (
          <ul className="space-y-1">
            {view.blockers.map((b) => (
              <li key={b.rule} className="text-warning">
                <code className="mr-2 rounded bg-bg-secondary px-1.5 py-0.5 text-xs">{b.rule}</code>
                {b.message}
              </li>
            ))}
          </ul>
        )}

        <Section title="Leaving date — setting this generates the offboarding checklist">
          <input
            type="date"
            value={p.endedOn ?? ""}
            onChange={(e) => patch.mutate({ endedOn: e.target.value || null })}
            className="rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent"
          />
        </Section>

        <CheckList
          title="Onboarding"
          checks={onboarding}
          onToggle={(id, on) => check.mutate({ id, on })}
        />

        {offboarding.length > 0 && (
          <CheckList
            title="Offboarding"
            checks={offboarding}
            onToggle={(id, on) => check.mutate({ id, on })}
          />
        )}

        <Section title="Security training">
          <ul className="mb-2 space-y-1">
            {view.training.map((t) => (
              <li key={t.id} className="text-text-secondary">
                <i className="fa-solid fa-circle-check mr-2 text-success" />
                {t.topic} · {t.completedOn}
              </li>
            ))}
            {view.training.length === 0 && <li className="text-text-muted">None recorded.</li>}
          </ul>
          <div className="flex flex-wrap gap-2">
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className={`${inputCls} max-w-xs`}
              placeholder="Topic"
            />
            <input
              type="date"
              value={trainedOn}
              onChange={(e) => setTrainedOn(e.target.value)}
              className={`${inputCls} max-w-[10rem]`}
            />
            <button
              onClick={() => train.mutate()}
              disabled={!topic.trim() || train.isPending}
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              Record training
            </button>
          </div>
        </Section>

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

function CheckList({
  title,
  checks,
  onToggle,
}: {
  title: string;
  checks: PersonView["checks"];
  onToggle: (id: string, completedOn: string | null) => void;
}) {
  const done = checks.filter((c) => c.completedOn).length;
  return (
    <Section title={`${title} — ${done}/${checks.length}`}>
      <ul className="space-y-1">
        {checks.map((c) => (
          <li key={c.id} className="flex items-center gap-2.5">
            <input
              type="checkbox"
              checked={!!c.completedOn}
              onChange={(e) => onToggle(c.id, e.target.checked ? today() : null)}
              className="shrink-0"
            />
            <span className={c.completedOn ? "text-text-secondary" : "text-text-heading"}>
              {c.label}
            </span>
            {c.controlRef && (
              <span className="rounded-full bg-bg-secondary px-2 py-0.5 text-[11px] text-text-muted">
                {c.controlRef}
              </span>
            )}
            {c.completedOn && (
              <span className="ml-auto flex shrink-0 items-center gap-2 text-xs text-text-muted">
                <input
                  type="date"
                  value={c.completedOn}
                  onChange={(e) => onToggle(c.id, e.target.value || null)}
                  className="rounded border border-border bg-bg-card px-1.5 py-0.5 text-xs text-text-secondary"
                  title="Back-date to when it actually happened"
                />
                {c.completedBy?.name ?? ""}
              </span>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
        {title}
      </h3>
      {children}
    </div>
  );
}
